/** Display currencies for browse/booking — PKR, USD, GBP only (product policy). */
export const DISPLAY_CURRENCIES = [
  { code: "PKR", label: "Pakistani Rupee", symbol: "Rs", locale: "en-PK", rateFromUsd: 280, decimals: 0 },
  { code: "USD", label: "US Dollar", symbol: "$", locale: "en-US", rateFromUsd: 1, decimals: 0 },
  { code: "GBP", label: "British Pound", symbol: "£", locale: "en-GB", rateFromUsd: 0.79, decimals: 0 },
] as const;

/**
 * PKR-only for now. Foreign currency is not shown to visitors.
 *
 * The USD figures are still stored on every room and tour and are untouched —
 * this only stops them being displayed. Flip back to true to re-enable the
 * PKR / USD / GBP selector when foreign pricing is ready to go public.
 *
 * With this false, DisplayPrice renders nothing for an item that has no
 * canonical pricePkr rather than converting from USD through the static
 * rateFromUsd table below. That table is an approximation, and a converted
 * figure is a rate nobody approved — 43 of 50 rooms disagreed with their real
 * PKR rate when /book was pricing that way.
 */
export const PUBLIC_CURRENCY_SELECTOR_ENABLED = false;

export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number]["code"];

export type CheckoutCurrency = "PKR" | "USD";

export const USD_TO_PKR = 280;

const STORAGE_KEY = "pbd-currency";

const currencyMap = Object.fromEntries(DISPLAY_CURRENCIES.map((c) => [c.code, c])) as Record<
  DisplayCurrency,
  (typeof DISPLAY_CURRENCIES)[number]
>;

export function isDisplayCurrency(value: string): value is DisplayCurrency {
  return value in currencyMap;
}

export function readStoredCurrency(): DisplayCurrency {
  if (typeof window === "undefined") return "PKR";
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isDisplayCurrency(saved)) return saved;
  } catch {
    /* ignore */
  }
  return "PKR";
}

export function persistCurrency(currency: DisplayCurrency) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, currency);
    document.cookie = `${STORAGE_KEY}=${currency};path=/;max-age=31536000;sameSite=lax`;
  } catch {
    /* ignore */
  }
}

/** Backend checkout supports PKR and USD only. */
export function checkoutCurrency(display: DisplayCurrency): CheckoutCurrency {
  return display === "PKR" ? "PKR" : "USD";
}

export function convertUsdToDisplay(amountUsd: number, currency: DisplayCurrency) {
  const meta = currencyMap[currency];
  const raw = amountUsd * meta.rateFromUsd;
  return meta.decimals === 0 ? Math.round(raw) : Math.round(raw * 100) / 100;
}

export function convertMoneyToDisplay(
  amount: number,
  sourceCurrency: CheckoutCurrency,
  displayCurrency: DisplayCurrency,
) {
  if (sourceCurrency === "PKR" && displayCurrency === "PKR") return amount;
  const amountUsd = sourceCurrency === "USD" ? amount : amount / USD_TO_PKR;
  return convertUsdToDisplay(amountUsd, displayCurrency);
}

/** Every currency renders as its unambiguous 3-letter ISO code, never a
 *  symbol — "$"/"¥" are each shared by multiple currencies in
 *  DISPLAY_CURRENCIES (USD/AUD/CAD/SGD; CNY/JPY), and a bare symbol next to
 *  a converted figure invites exactly the kind of mismatched-currency
 *  confusion this formatter exists to prevent. */
function formatDisplayAmount(amount: number, currency: DisplayCurrency) {
  const meta = currencyMap[currency];
  const formatted = amount.toLocaleString(meta.locale, {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
  });
  return `${currency} ${formatted}`;
}

/** PKR is the one canonical, exactly-quoted currency (it's what the
 *  reservations team actually charges — see DisplayPrice's pricePkr prop).
 *  Every other display currency is necessarily an FX-snapshot conversion,
 *  so it's labelled "Approx." — including USD, even where a tour/room also
 *  has its own real basePriceUSD, since the display layer has no way to
 *  know that coincidence and must not imply a precision it can't promise
 *  for the general case. */
function withApproxPrefix(label: string, displayCurrency: DisplayCurrency) {
  return displayCurrency === "PKR" ? label : `Approx. ${label}`;
}

export function formatMoneyAmount(
  amount: number,
  sourceCurrency: CheckoutCurrency,
  displayCurrency: DisplayCurrency,
  options?: { prefix?: string; suffix?: string },
) {
  const converted = convertMoneyToDisplay(amount, sourceCurrency, displayCurrency);
  let label = withApproxPrefix(formatDisplayAmount(converted, displayCurrency), displayCurrency);
  if (options?.prefix) label = `${options.prefix} ${label}`;
  if (options?.suffix) label = `${label} ${options.suffix}`;
  return label;
}

export function formatUsdAmount(
  amountUsd: number,
  currency: DisplayCurrency,
  options?: { prefix?: string; suffix?: string },
) {
  const converted = convertUsdToDisplay(amountUsd, currency);
  let label = withApproxPrefix(formatDisplayAmount(converted, currency), currency);

  if (options?.prefix) label = `${options.prefix} ${label}`;
  if (options?.suffix) label = `${label} ${options.suffix}`;
  return label;
}

export function parseUsdLabel(label: string): number | null {
  const match = label.replace(/,/g, "").match(/([\d.]+)/);
  if (!match) return null;
  const value = Number.parseFloat(match[1]!);
  return Number.isFinite(value) ? value : null;
}

export function displayCurrencyLabel(code: DisplayCurrency) {
  return currencyMap[code].label;
}
